/**
 * Tabsbar version 1.0
 * Noam
 *
 * Tabsbar is a bar of butttons where only one can be selected at a time, the buttons can also have a check box
 * to enable/disable the data related to it.
 * It is in use in the studio for the states, and at the engagement window for the different survey tabs.
 *
 * API:
 * New accepts a selectable collection of type TabsBar.TabCollection
 *    in which each element model has the following attributes
 *    id  : The Id of the button
 *    name: string for name
 *    tooltip:  string for tooltip
 *    hasCheckbox : boolean : Each button can have an a checkbox, this configures if to show checkbox on the button
 *    hasContent : Boolean: Permutation B of the tabs - show different colors when the tab has content
 *    checkBoxValue: the checkbox value
 *    checkboxEnabled: is the checkbox enabled or disabled
 *
 *
 * The selectable collection should be initalized with its selectedID
 *
 * getSelectedTabId() - get the selected Tab
 *
 * Usage example
 * var items = this.engagement.getEngagementAllowedInstanceTypes();
 * var instanceTabs = _.map(items, function (item) {
 *               var instanceType = EngagementModel.DISPLAY_INSTANCE_TYPES[item.type];
 *               return {
 *                   id:    instanceType, //put the enum number not the string,
 *                   name:  LE.translator.translate("LECampaigns.studio.instancesbar." + item.type.toLowerCase() + ".name"),
 *                   tooltip:  LE.translator.translate("LECampaigns.studio.instancesbar." + item.type.toLowerCase() + ".tooltip"),
 *                   hasCheckbox: item.hasCheckbox,
 *                   checkBoxValue: this.engagement.getDisplayInstanceEnabled(this.engagement.getDisplayInstanceIndexByType(instanceType))
 *               };
 *           }, this);
 *
 * var collection = SelectableCollectionDecorator.decorate(new TabsBar.TabCollection(instanceTabs,{}),this.studioModel.getSelectedDisplayInstanceType());
 * this.tabsBarView = new TabsBar({collection: collection});
 *

 */

define(function (require) {
    "use strict";

    var $ = require("jquery");
    var _ = require("underscore");
    var Marionette = require("marionette");
    var tabsTemplate = require("ui/tabsbar/templates/tabsbar.tmpl");
    var TabsItemView = require("ui/tabsbar/js/views/TabItemView");
    var TabCollection = require("ui/tabsbar/js/collections/TabCollection");
    var TabModel = require("ui/tabsbar/js/models/TabModel");
    var Button = require("ui/button/Button");

    var TabsBar;

    /*
     this view represents the different engagement states (currently online, offline, etc.)
     */
    TabsBar = Marionette.CompositeView.extend({
        itemView: TabsItemView,
        itemViewContainer: ".tabsbar",
        className: "tabs-wrapper",
        template: tabsTemplate,
        events: {},
        modelEvents: {},
        ui:{
            barContainer: "ul.tabsbar"
        },


        initialize: function (options) {
            options = options || {};
            this.itemViewOptions = {continueOnTabChange: options.continueOnTabChange};

            this.permutation = options.permutation || TabsBar.PERMUTATION.A;
            // determine if the component change the selected tab ui and trigger the event (like in edit canned content) or just trigger the event and not change the ui (like in header tabs in users). default is TRUE - change + trigger.
            this.commitStateChange = _.has(options, "commitStateChange") ? options.commitStateChange : true;
            this.bindEvents();
        },
        bindEvents: function () {
            this.on("itemview:checkbox:clicked", this._updateSelectedCheckbox);
            this.on("itemview:tab:changed"     , this._updateSelectedTab);
            this.on("itemview:button:clicked"  , this._tabButtonClicked);
            this.listenTo(this.collection,"change:selection",this._collectionSelectionChanged);
            /* uncomment if you want to implement the listening to reset of the collection */
            //this.listenTo(this.collection,"reset",this._onCollectionReset);
        },

        unbindEvents: function() {
            this.off("itemview:checkbox:clicked");
            this.off("itemview:tab:changed");
            this.off("itemview:button:clicked");
        },

        _updateSelectedCheckbox: function(childView, model){
            this.trigger("change:tabCheckbox", model.id, model.get("checkBoxValue"));
        },
        _tabButtonClicked: function(childView, model){
            this.trigger("click:tabButton", model.id);
        },
        _updateSelectedTab: function (childView, model) {
            if (!this.collection.isSelected(model.id)) {
                if (this.commitStateChange) {
                    this.collection.selectModel(model.id);
                    this._updateSelectionOnView(childView); // todo: make sure if we need it or selectModel is enough
                }
                this.trigger("change:selection", model.id);
            }
        },
        _updateSelectionOnView: function (selectedView) {
            this.children.each(function (view) {
                if (view != selectedView) {
                    view.removeClass("selected-tab");
                    view.$el.attr("aria-selected", false);
                } else {
                    view.addClass("selected-tab");
                    view.$el.attr("aria-selected", true);
                }
            });
        },
        _setSelectedTabClass: function(){
            this.ui.barContainer.addClass(this.permutation);
            var that = this;
            var firstView = null;
            var lastView = null;

            this.children.each(function (view) {
                lastView = view;
                if (_.isNull(firstView)) {
                    firstView = view;
                }
                //add selected class
                if (view.model.id == that.getSelectedTabId()) {
                    view.addClass("selected-tab");
                } else {
                    view.removeClass("selected-tab");
                }

            });

            //add first class
            firstView.addClass("first");
            //add end class
            lastView.addClass("last");
        },
        //to implement if we need to apply relevant class when collection is reset
        /*_onCollectionReset: function(){
         _.defer(_.bind(function(){
         this._setSelectedTabClass();
         }, this));
         },*/
        _collectionSelectionChanged: function () {
            var selectedModel = this.collection.getSelectedModel();
            if(selectedModel) {
                var v = this.children.findByModel(selectedModel);
                this._updateSelectionOnView(v);
            }
        },
        _calculateTabWidth: function() {
            if (this.permutation === TabsBar.PERMUTATION.D) {
                this.children.each(_.bind(function (view) {
                    view.addClass("tab-width-" + this.collection.size());
                }, this));
            }
        },

        onRender: function () {
            this._setSelectedTabClass();
            this._calculateTabWidth();
        },
        getSelectedTabId: function () {
            return this.collection.getSelectedId();
        },
        setErrorOnTab: function (isError) {
            var selectedModel = this.collection.getSelectedModel();
            if(selectedModel) {
                var selectedTab = this.children.findByModel(selectedModel);
                selectedTab.setError(isError);
            }
        },
        onClose: function() {
            this.unbindEvents();
        }
    });

    TabsBar.TabCollection = TabCollection;
    TabsBar.TabModel = TabModel;
    /**
     * Public static eNum of the optional permutation.
     * perm-A: checkbox with label.
     * perm-B: checkbox without label.
     */
    TabsBar.PERMUTATION = {
        A : "perm-A",
        B : "perm-B",
        C : "perm-C",
        D : "perm-D"
    };

    return TabsBar;
});
